import * as Tabs from '@radix-ui/react-tabs'
import {
  AlertTriangle,
  Ban,
  CalendarClock,
  CheckCircle2,
  CircleSlash2,
  Clock3,
  Copy,
  FileClock,
  GitCompareArrows,
  History,
  HelpCircle,
  ListChecks,
  Maximize2,
  Minimize2,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Badge, Card, CardContent } from '@/components'
import {
  useMeetingCenterHistory,
  useMeetingCenterLatest,
  useMeetingCenterSnapshot,
} from '@/hooks/useMeetingCenter'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import { ApiError } from '@/lib/api'
import { meetingUnknownReasonPresentation } from '@/lib/meeting-center-presentation'
import { cn } from '@/lib/utils'
import type {
  EvidenceBackedText,
  EvidenceRef,
  HandoffReadiness,
  MeetingAudience,
  MeetingEvidenceState,
  MeetingScheduleState,
  MeetingSnapshot,
  MeetingSnapshotLifecycle,
  OutcomeGateSet,
  RuntimeHealth,
  SourceRun,
  Workstream,
} from '@/types/meeting-center'

type BaseMeetingTab = 'current' | 'actions' | 'sources' | 'history'
export type MeetingCenterSurface = 'weekly-status' | 'meeting-center' | 'evidence-history'

const PRESENTATION_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const SURFACE_COPY: Record<
  MeetingCenterSurface,
  { title: string; description: string; defaultTab: BaseMeetingTab }
> = {
  'weekly-status': {
    title: '今週の状況',
    description: '3つの取組の現在地、期限、停止要因、担当、情報鮮度を確認します。',
    defaultTab: 'current',
  },
  'meeting-center': {
    title: '週次会議',
    description: '共通の事実と根拠を確認し、今週の判断と担当を決めます。',
    defaultTab: 'current',
  },
  'evidence-history': {
    title: '根拠・履歴',
    description: '情報源の取得状態、確認時刻、過去の改訂を確認します。',
    defaultTab: 'sources',
  },
}

export function buildMeetingQuestionPrompt(
  snapshot: MeetingSnapshot,
  audience: MeetingAudience,
): string {
  const perspective = audience === 'executive' ? '役員会議' : '現場会議'
  const decisions = snapshot.decisions
    .filter((decision) => decision.state === 'proposed')
    .slice(0, 3)
    .map((decision, index) => `${index + 1}. ${decision.summary}`)
  const workstreams = snapshot.workstreams
    .map((workstream) => `- ${workstream.title}: 日程=${SCHEDULE_LABELS[workstream.scheduleState]}`)

  return [
    `${snapshot.weekOf}の${perspective}について確認してください。`,
    '次の取組ごとに、今週決めること、未解決事項、担当と期限の不足を指摘してください。',
    ...workstreams,
    ...(decisions.length > 0 ? ['判断候補:', ...decisions] : ['判断候補: 未登録']),
    '根拠が確認できない項目は推測せず「未確認」としてください。',
  ].join('\n')
}

function workstreamTabValue(workstreamId: string): string {
  return `workstream:${workstreamId}`
}

const LIFECYCLE_LABELS: Record<MeetingSnapshotLifecycle, string> = {
  draft: '下書き',
  ready: '会議準備済み',
  locked: '確定',
  superseded: '改訂済み',
  blocked: '準備停止中',
}

const SCHEDULE_LABELS: Record<MeetingScheduleState, string> = {
  on_track: '予定どおり',
  attention: '要確認',
  blocked: '停止中',
  unknown: '未確認',
}

const STATE_LABELS: Record<MeetingEvidenceState, string> = {
  verified: '確認済み',
  stale: '更新期限超過',
  unknown: '未確認',
  absent: '存在なし',
  not_configured: '未設定',
  denied: '権限により非表示',
  error: '取得エラー',
  conflict: '情報が不一致',
}

const DISPLAY_TIME_ZONE = 'Asia/Tokyo'

function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00+09:00`)
  if (Number.isNaN(date.getTime())) return '対象週不明'
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  }).format(date)
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '時刻未確認'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '時刻未確認'
  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: DISPLAY_TIME_ZONE,
  }).format(date)
}

function stateTone(state: MeetingEvidenceState): 'success' | 'warning' | 'destructive' | 'muted' {
  if (state === 'verified' || state === 'absent') return 'success'
  if (state === 'stale' || state === 'unknown' || state === 'not_configured') return 'warning'
  if (state === 'denied') return 'muted'
  return 'destructive'
}

function StateIcon({ state, className }: { state: MeetingEvidenceState; className?: string }) {
  const props = { className: cn('h-4 w-4 shrink-0', className), 'aria-hidden': true }
  if (state === 'verified') return <CheckCircle2 {...props} />
  if (state === 'stale') return <Clock3 {...props} />
  if (state === 'unknown') return <HelpCircle {...props} />
  if (state === 'absent') return <CircleSlash2 {...props} />
  if (state === 'not_configured') return <FileClock {...props} />
  if (state === 'denied') return <Ban {...props} />
  if (state === 'conflict') return <GitCompareArrows {...props} />
  return <XCircle {...props} />
}

function StateBadge({ state }: { state: MeetingEvidenceState }) {
  return (
    <Badge tone={stateTone(state)} className="rounded-md">
      <StateIcon state={state} />
      {STATE_LABELS[state]}
    </Badge>
  )
}

function ScheduleBadge({ state }: { state: MeetingScheduleState }) {
  const tone = state === 'on_track'
    ? 'success'
    : state === 'attention' || state === 'unknown'
      ? 'warning'
      : 'destructive'
  return (
    <Badge tone={tone} className="rounded-md">
      {state === 'on_track' ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <AlertTriangle className="h-4 w-4" aria-hidden />}
      {SCHEDULE_LABELS[state]}
    </Badge>
  )
}

function EvidenceBackedSchedule({
  workstream,
  onOpenSources,
}: {
  workstream: Workstream
  onOpenSources: () => void
}) {
  const resolved = resolveClaim(workstream.schedule, workstream.evidenceRefs)
  if (!resolved.displayable) {
    return (
      <div className="space-y-1">
        <StateBadge state={resolved.state} />
        <p className="text-xs text-muted-foreground">{resolved.reasonLabel}</p>
        {resolved.evidenceCount > 0 && (
          <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
        )}
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <ScheduleBadge state={workstream.scheduleState} />
      {resolved.state !== 'verified' && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StateBadge state={resolved.state} />
          <span>{resolved.reasonLabel}</span>
          <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
        </div>
      )}
    </div>
  )
}

function ownerLabel(ownerRef: string | null, ownerDisplayName?: string | null): string {
  if (ownerDisplayName?.trim()) return ownerDisplayName.trim()
  return ownerRef ? '担当設定済み' : '担当未設定'
}

interface ProgressSummary {
  completed: number
  total: number
  percent: number | null
  approved: boolean
  evidenceCount: number
  completedWeight: number
  totalWeight: number
  partial: boolean
}

function progressForGateSet(
  gateSet: OutcomeGateSet,
  workstream: Workstream,
): ProgressSummary {
  const active = gateSet.gates.filter((gate) => gate.active)
  const denominatorIsApproved = Boolean(
    gateSet.approved &&
    gateSet.version &&
    gateSet.approvedBy &&
    gateSet.approvedAt &&
    gateSet.definitionHash &&
    active.length > 0,
  )
  if (!denominatorIsApproved) {
    return {
      completed: 0,
      total: active.length,
      percent: null,
      approved: false,
      evidenceCount: 0,
      completedWeight: 0,
      totalWeight: 0,
      partial: false,
    }
  }

  const evidenceById = new Map(
    workstream.evidenceRefs.map((item) => [item.evidenceId, item] as const),
  )
  const completed = active.filter((gate) =>
    gate.status === 'completed' &&
    gate.completionEvidenceIds.length > 0 &&
    gate.completionEvidenceIds.every(
      (evidenceId) => evidenceById.get(evidenceId)?.state === 'verified',
    ),
  )
  const totalWeight = active.reduce((sum, gate) => sum + gate.weight, 0)
  const completedWeight = completed.reduce((sum, gate) => sum + gate.weight, 0)
  const percent = totalWeight > 0
    ? Math.round((completedWeight / totalWeight) * 10_000) / 100
    : null

  return {
    completed: completed.length,
    total: active.length,
    percent,
    approved: true,
    evidenceCount: new Set(completed.flatMap((gate) => gate.completionEvidenceIds)).size,
    completedWeight,
    totalWeight,
    partial: false,
  }
}

function aggregateProgress(snapshot: MeetingSnapshot): ProgressSummary {
  const allSummaries = snapshot.workstreams
    .map((workstream) => progressForGateSet(workstream.outcomeGateSet, workstream))
  const summaries = allSummaries.filter((summary) => summary.approved)
  const partial = allSummaries.some((summary) => !summary.approved)
  if (summaries.length === 0) {
    return {
      completed: 0,
      total: 0,
      percent: null,
      approved: false,
      evidenceCount: 0,
      completedWeight: 0,
      totalWeight: 0,
      partial,
    }
  }
  const completed = summaries.reduce((sum, summary) => sum + summary.completed, 0)
  const total = summaries.reduce((sum, summary) => sum + summary.total, 0)
  const completedWeight = summaries.reduce((sum, summary) => sum + summary.completedWeight, 0)
  const totalWeight = summaries.reduce((sum, summary) => sum + summary.totalWeight, 0)
  return {
    completed,
    total,
    percent: !partial && totalWeight > 0
      ? Math.round((completedWeight / totalWeight) * 10_000) / 100
      : null,
    approved: true,
    evidenceCount: summaries.reduce((sum, summary) => sum + summary.evidenceCount, 0),
    completedWeight,
    totalWeight,
    partial,
  }
}

function sourceCoverage(snapshot: MeetingSnapshot) {
  return {
    verified: snapshot.requiredVerifiedSourceCount,
    total: snapshot.requiredSourceCount,
  }
}

function sourceSummaryState(snapshot: MeetingSnapshot): MeetingEvidenceState {
  return snapshot.requiredSourceState
}

interface ResolvedClaim {
  displayable: boolean
  text: string | null
  state: MeetingEvidenceState
  observedAt: string | null
  evidenceCount: number
  reasonLabel: string
}

function resolveClaim(claim: EvidenceBackedText, evidenceRefs: EvidenceRef[]): ResolvedClaim {
  const text = claim.text?.trim() || null
  const evidenceIds = [...new Set(claim.evidenceIds)]
  const evidenceById = new Map(evidenceRefs.map((item) => [item.evidenceId, item] as const))
  const refs = evidenceIds.map((evidenceId) => evidenceById.get(evidenceId))
  const presentRefs = refs.filter((ref): ref is EvidenceRef => Boolean(ref))
  const allResolved = refs.length > 0 && presentRefs.length === refs.length
  const unknownPresentation = claim.unknownReason
    ? meetingUnknownReasonPresentation(claim.unknownReason)
    : null
  const stateOrder: MeetingEvidenceState[] = [
    'conflict',
    'error',
    'denied',
    'stale',
    'unknown',
    'not_configured',
    'absent',
    'verified',
  ]
  const state = unknownPresentation?.state ?? (allResolved
    ? stateOrder.find((candidate) => presentRefs.some((ref) => ref.state === candidate)) ?? 'unknown'
    : 'unknown')
  const displayable = Boolean(
    !unknownPresentation
      && text
      && allResolved
      && (state === 'verified' || state === 'stale' || state === 'conflict'),
  )
  const stateReason: Partial<Record<MeetingEvidenceState, string>> = {
    stale: '根拠が更新期限を超えています',
    conflict: '複数の根拠が一致していません',
    error: '根拠を取得できていません',
    denied: '根拠は権限により非表示です',
    not_configured: '必要な情報源が未設定です',
    absent: '対象の存在を確認できません',
    unknown: '確認できる根拠がありません',
  }

  if (!displayable) {
    return {
      displayable: false,
      text: null,
      state,
      observedAt: presentRefs
        .map((ref) => ref.observedAt)
        .filter((value): value is string => Boolean(value))
        .sort()
        .at(-1) ?? null,
      evidenceCount: presentRefs.length,
      reasonLabel: unknownPresentation?.label || stateReason[state] || '確認できる根拠がありません',
    }
  }
  const observedAt = presentRefs
    .map((ref) => ref.observedAt)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1) ?? null

  return {
    displayable: true,
    text,
    state,
    observedAt,
    evidenceCount: presentRefs.length,
    reasonLabel: stateReason[state] ?? '',
  }
}

function EvidenceDrillthrough({
  count,
  onOpenSources,
}: {
  count: number
  onOpenSources: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpenSources}
      className="rounded-sm text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px"
    >
      情報源で根拠 {count}件を確認
    </button>
  )
}

function EvidenceBackedValue({
  claim,
  evidenceRefs,
  onOpenSources,
  compact = false,
  allowDrillthrough = true,
}: {
  claim: EvidenceBackedText
  evidenceRefs: EvidenceRef[]
  onOpenSources: () => void
  compact?: boolean
  allowDrillthrough?: boolean
}) {
  const resolved = resolveClaim(claim, evidenceRefs)
  if (!resolved.displayable) {
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium text-yellow-600">根拠未確認</p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StateBadge state={resolved.state} />
          <span>{resolved.reasonLabel}</span>
          {allowDrillthrough && resolved.evidenceCount > 0 && (
            <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-sm">{resolved.text}</p>
      {(resolved.state !== 'verified' || !compact) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StateBadge state={resolved.state} />
          {resolved.state !== 'verified' && <span>{resolved.reasonLabel}</span>}
          {!compact && <span className="tabular-nums">確認 {formatDateTime(resolved.observedAt)}</span>}
          {allowDrillthrough
            ? <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
            : <span>根拠 {resolved.evidenceCount}件</span>}
        </div>
      )}
    </div>
  )
}

function EvidenceBackedList({
  items,
  empty,
  evidenceRefs,
  onOpenSources,
}: {
  items: EvidenceBackedText[]
  empty: string
  evidenceRefs: EvidenceRef[]
  onOpenSources: () => void
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{empty}</p>
  }
  return (
    <ul className="space-y-3">
      {items.map((item, index) => {
        const resolved = resolveClaim(item, evidenceRefs)
        return (
          <li
            key={`${item.text}-${index}`}
            className="flex gap-2"
            data-evidence-list-state={resolved.state}
          >
            <StateIcon
              state={resolved.state}
              className={cn(
                'mt-0.5 text-primary',
                resolved.state !== 'verified' && 'text-yellow-600',
              )}
            />
            <EvidenceBackedValue
              claim={item}
              evidenceRefs={evidenceRefs}
              onOpenSources={onOpenSources}
            />
          </li>
        )
      })}
    </ul>
  )
}

function operationalBadge(
  kind: 'runtime' | 'handoff',
  indicator: RuntimeHealth | HandoffReadiness,
) {
  if (kind === 'runtime') {
    const state = (indicator as RuntimeHealth).state
    const label = state === 'healthy' ? '稼働確認済み' : state === 'degraded' ? '要注意' : state === 'down' ? '停止' : '未確認'
    const tone = state === 'healthy' ? 'success' : state === 'down' ? 'destructive' : 'warning'
    return { label, tone } as const
  }
  const state = (indicator as HandoffReadiness).state
  const label = state === 'ready' ? '引継ぎ準備済み' : state === 'attention' ? '要確認' : state === 'blocked' ? '停止中' : '未確認'
  const tone = state === 'ready' ? 'success' : state === 'blocked' ? 'destructive' : 'warning'
  return { label, tone } as const
}

function IndicatorBlock({
  title,
  kind,
  indicator,
  evidenceRefs,
  onOpenSources,
}: {
  title: string
  kind: 'runtime' | 'handoff'
  indicator: RuntimeHealth | HandoffReadiness
  evidenceRefs: EvidenceRef[]
  onOpenSources: () => void
}) {
  const badge = operationalBadge(kind, indicator)
  const resolved = resolveClaim(indicator, evidenceRefs)
  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      {resolved.displayable ? (
        <>
          <Badge tone={badge.tone} className="rounded-md">{badge.label}</Badge>
          <p className="text-sm">{resolved.text}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StateBadge state={resolved.state} />
            {resolved.state !== 'verified' && <span>{resolved.reasonLabel}</span>}
            <span className="tabular-nums">確認 {formatDateTime(resolved.observedAt)}</span>
            <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
          </div>
        </>
      ) : (
        <div>
          <Badge tone="warning" className="rounded-md">未確認</Badge>
          <p className="text-sm font-medium text-yellow-600">根拠未確認</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StateBadge state={resolved.state} />
            <span>{resolved.reasonLabel}</span>
            {resolved.evidenceCount > 0 && (
              <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ProgressText({
  workstream,
  onOpenSources,
}: {
  workstream: Workstream
  onOpenSources: () => void
}) {
  const progress = progressForGateSet(workstream.outcomeGateSet, workstream)
  if (!progress.approved) {
    return (
      <div>
        <p className="font-medium text-yellow-600">成果ゲート定義中</p>
        <p className="mt-1 text-xs text-muted-foreground">割合は表示できません</p>
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <p className="font-semibold tabular-nums">
        検証済み {progress.completed}/{progress.total}
      </p>
      {progress.percent !== null && (
        <p className="text-xs tabular-nums text-muted-foreground">{progress.percent}%</p>
      )}
      {progress.evidenceCount > 0 && (
        <EvidenceDrillthrough count={progress.evidenceCount} onOpenSources={onOpenSources} />
      )}
    </div>
  )
}

type ComparisonColumn = 'title' | 'progress' | 'schedule' | 'issue' | 'owner'

const COMPARISON_LABELS: Record<ComparisonColumn, string> = {
  title: '取組',
  progress: '成果ゲート',
  schedule: '日程',
  issue: '最大課題',
  owner: '担当',
}

function ComparisonValue({
  column,
  workstream,
  onOpenSources,
}: {
  column: ComparisonColumn
  workstream: Workstream
  onOpenSources: () => void
}) {
  if (column === 'title') return <span className="font-medium">{workstream.title}</span>
  if (column === 'progress') return <ProgressText workstream={workstream} onOpenSources={onOpenSources} />
  if (column === 'schedule') return <EvidenceBackedSchedule workstream={workstream} onOpenSources={onOpenSources} />
  if (column === 'owner') return <span>{ownerLabel(workstream.ownerRef, workstream.ownerDisplayName)}</span>
  if (workstream.issues.length === 0) {
    return <span className="text-muted-foreground">課題は未登録です。有無は未確認です</span>
  }
  return (
    <EvidenceBackedValue
      claim={workstream.issues[0]}
      evidenceRefs={workstream.evidenceRefs}
      onOpenSources={onOpenSources}
      compact
    />
  )
}

function WorkstreamComparison({
  snapshot,
  audience,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  audience: MeetingAudience
  onOpenSources: () => void
}) {
  const columns: ComparisonColumn[] = audience === 'executive'
    ? ['title', 'progress', 'schedule', 'issue', 'owner']
    : ['title', 'owner', 'issue', 'schedule', 'progress']
  return (
    <section aria-labelledby="comparison-title" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id="comparison-title" className="font-semibold">取組の現在地</h2>
        <span className="text-xs text-muted-foreground">成果と日程を分けて表示</span>
      </div>

      <div className="hidden overflow-x-auto rounded-lg border md:block">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-4 py-3 font-medium">{COMPARISON_LABELS[column]}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {snapshot.workstreams.map((workstream) => (
              <tr key={workstream.workstreamId}>
                {columns.map((column) => (
                  <td key={column} className={cn('px-4 py-3 align-top', column === 'issue' && 'max-w-xs')}>
                    <ComparisonValue column={column} workstream={workstream} onOpenSources={onOpenSources} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 md:hidden">
        {snapshot.workstreams.map((workstream) => (
          <div key={workstream.workstreamId} className="rounded-lg border bg-card p-4 shadow-sm">
            <h3 className="font-medium">{workstream.title}</h3>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
              {columns.filter((column) => column !== 'title').map((column) => (
                <div key={column} className={column === 'issue' ? 'col-span-2' : undefined}>
                  <dt className="text-xs text-muted-foreground">{COMPARISON_LABELS[column]}</dt>
                  <dd className="mt-1">
                    <ComparisonValue column={column} workstream={workstream} onOpenSources={onOpenSources} />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  )
}

function ExecutiveFocus({
  snapshot,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  onOpenSources: () => void
}) {
  const decisions = snapshot.decisions.filter((item) => item.state === 'proposed').slice(0, 3)
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <section data-testid="executive-focus" className="space-y-3">
      <div className="flex items-center gap-2">
        <ListChecks className="h-5 w-5 text-primary" aria-hidden />
        <h2 className="font-semibold">今週のTop判断</h2>
      </div>
      {decisions.length > 0 ? (
        <ol className="grid gap-2">
          {decisions.map((decision, index) => (
            <li key={decision.decisionId} className="grid grid-cols-[1.75rem_1fr] gap-2 rounded-md border bg-card px-3 py-2 text-sm">
              <span className="font-semibold tabular-nums text-primary">{index + 1}</span>
              <EvidenceBackedValue
                claim={{
                  text: decision.summary,
                  evidenceIds: decision.evidenceIds,
                  unknownReason: decision.unknownReason,
                }}
                evidenceRefs={evidenceRefs}
                onOpenSources={onOpenSources}
              />
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">今週の判断事項は未登録です</p>
      )}
    </section>
  )
}

function FieldFocus({
  snapshot,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  onOpenSources: () => void
}) {
  const actions = snapshot.actionItems
    .filter((item) => item.status === 'open' || item.status === 'blocked')
    .slice(0, 3)
  const blockers = snapshot.workstreams
    .filter((workstream) => workstream.scheduleState === 'blocked' || workstream.issues.length > 0)
    .slice(0, 3)
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <section data-testid="field-focus" className="space-y-3">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-primary" aria-hidden />
        <h2 className="font-semibold">今週の担当・期限・課題</h2>
      </div>
      {actions.length === 0 && blockers.length === 0 ? (
        <p className="text-sm text-muted-foreground">未完了アクション・課題は未登録です。有無は未確認です</p>
      ) : (
        <div className="grid gap-2">
          {actions.map((action) => (
            <div key={action.actionItemId} className="rounded-md border bg-card px-3 py-2 text-sm">
              {resolveClaim({ text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }, evidenceRefs).displayable && (
                <div className="mb-2 flex items-start justify-between gap-3">
                  <span className="text-xs text-muted-foreground">
                    担当 {ownerLabel(action.ownerRef, action.ownerDisplayName)}
                    <span className="mx-2" aria-hidden>/</span>
                    期限 <span className="tabular-nums">{formatDateTime(action.dueAt)}</span>
                  </span>
                  <Badge tone={action.status === 'blocked' ? 'destructive' : 'warning'} className="rounded-md">
                    {action.status === 'blocked' ? '停止中' : '未完了'}
                  </Badge>
                </div>
              )}
              <EvidenceBackedValue
                claim={{ text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }}
                evidenceRefs={evidenceRefs}
                onOpenSources={onOpenSources}
              />
            </div>
          ))}
          {blockers.map((workstream) => (
            <div key={workstream.workstreamId} className="rounded-md border bg-card px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">{workstream.title}の課題・停止要因</p>
              {workstream.issues.length > 0 ? (
                <div className="mt-1">
                  <EvidenceBackedValue
                    claim={workstream.issues[0]}
                    evidenceRefs={workstream.evidenceRefs}
                    onOpenSources={onOpenSources}
                    compact
                  />
                </div>
              ) : (
                <p className="mt-1 text-sm font-medium text-yellow-600">根拠未確認</p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function CurrentWeekPanel({
  snapshot,
  audience,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  audience: MeetingAudience
  onOpenSources: () => void
}) {
  const progress = aggregateProgress(snapshot)
  const coverage = sourceCoverage(snapshot)
  const summaryState = sourceSummaryState(snapshot)
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  const resolvedDelta = snapshot.previousWeekDelta?.text
    ? resolveClaim({
        text: snapshot.previousWeekDelta.text,
        evidenceIds: snapshot.previousWeekDelta.evidenceIds,
        unknownReason: snapshot.previousWeekDelta.unknownReason,
      }, evidenceRefs)
    : null
  const focusPanels = audience === 'executive'
    ? [<ExecutiveFocus key="executive" snapshot={snapshot} onOpenSources={onOpenSources} />, <FieldFocus key="field" snapshot={snapshot} onOpenSources={onOpenSources} />]
    : [<FieldFocus key="field" snapshot={snapshot} onOpenSources={onOpenSources} />, <ExecutiveFocus key="executive" snapshot={snapshot} onOpenSources={onOpenSources} />]

  return (
    <div className="space-y-6">
      <section className="grid gap-4 border-b pb-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.6fr)]">
        <div className="grid gap-5">{focusPanels}</div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-l-0 pt-1 text-sm lg:border-l lg:pl-5">
          <div>
            <p className="text-xs text-muted-foreground">成果ゲート</p>
            {progress.approved ? (
              <>
                {progress.partial && <p className="mt-1 font-medium text-yellow-600">一部分母定義中</p>}
                <p className="mt-1 font-semibold tabular-nums">検証済み {progress.completed}/{progress.total}</p>
                {progress.percent !== null && <p className="text-xs tabular-nums text-muted-foreground">{progress.percent}%</p>}
                {progress.evidenceCount > 0 && <EvidenceDrillthrough count={progress.evidenceCount} onOpenSources={onOpenSources} />}
              </>
            ) : (
              <p className="mt-1 font-medium text-yellow-600">定義中</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">情報確認率</p>
            <p className="mt-1 font-semibold tabular-nums">確認済み情報源 {coverage.verified}/{coverage.total}</p>
            <div className="mt-1"><StateBadge state={summaryState} /></div>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">先週からの変化</p>
            {snapshot.previousWeekDelta && snapshot.previousWeekDelta.state !== 'unknown' && resolvedDelta?.displayable ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone={snapshot.previousWeekDelta.state === 'changed' ? 'warning' : 'muted'} className="rounded-md">
                  {snapshot.previousWeekDelta.state === 'changed' ? '変化あり' : '変化なし'}
                </Badge>
                <span>{resolvedDelta.text}</span>
                <span className="text-xs tabular-nums text-muted-foreground">確認 {formatDateTime(snapshot.previousWeekDelta.observedAt)}</span>
                <EvidenceDrillthrough count={resolvedDelta.evidenceCount} onOpenSources={onOpenSources} />
              </div>
            ) : (
              <p className="mt-1 text-yellow-600">前週差分は未確認です</p>
            )}
          </div>
        </div>
      </section>

      <WorkstreamComparison snapshot={snapshot} audience={audience} onOpenSources={onOpenSources} />

      {summaryState !== 'verified' && (
        <div className="flex items-start gap-3 rounded-md border border-yellow-600/30 bg-yellow-600/5 p-3 text-sm" role="status">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600" aria-hidden />
          <div>
            <p className="font-medium">一部の情報源を確認できていません</p>
            <p className="mt-1 text-muted-foreground">未確認を進捗ゼロへ置き換えず、判断前に状態を確認してください。</p>
          </div>
        </div>
      )}
    </div>
  )
}

function WorkstreamPanel({
  workstream,
  onOpenSources,
}: {
  workstream: Workstream
  onOpenSources: () => void
}) {
  return (
        <Card>
          <div className="flex flex-col gap-3 border-b px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
            <div>
              <h2 className="font-semibold">{workstream.title}</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                担当 {ownerLabel(workstream.ownerRef, workstream.ownerDisplayName)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ProgressText workstream={workstream} onOpenSources={onOpenSources} />
              <EvidenceBackedSchedule workstream={workstream} onOpenSources={onOpenSources} />
            </div>
          </div>
          <CardContent className="space-y-6 p-4 md:p-6">
            <div className="grid gap-3 md:grid-cols-2">
              <IndicatorBlock
                title="稼働状態"
                kind="runtime"
                indicator={workstream.runtimeHealth}
                evidenceRefs={workstream.evidenceRefs}
                onOpenSources={onOpenSources}
              />
              <IndicatorBlock
                title="引継ぎ準備"
                kind="handoff"
                indicator={workstream.handoffReadiness}
                evidenceRefs={workstream.evidenceRefs}
                onOpenSources={onOpenSources}
              />
            </div>

            <dl className="grid gap-x-8 gap-y-5 md:grid-cols-2 xl:grid-cols-3">
              <div>
                <dt className="text-xs font-medium text-muted-foreground">100%の定義</dt>
                <dd className="mt-1">
                  <EvidenceBackedValue claim={workstream.definitionOf100Percent} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">現在地</dt>
                <dd className="mt-1">
                  <EvidenceBackedValue claim={workstream.currentState} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">目的</dt>
                <dd className="mt-1">
                  <EvidenceBackedValue claim={workstream.purpose} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">手法</dt>
                <dd className="mt-1">
                  <EvidenceBackedValue claim={workstream.method} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">期待結果</dt>
                <dd className="mt-1">
                  <EvidenceBackedValue claim={workstream.expectedResult} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">スケジュール</dt>
                <dd className="mt-1">
                  <EvidenceBackedValue claim={workstream.schedule} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
                </dd>
              </div>
            </dl>

            <div className="grid gap-6 border-t pt-5 md:grid-cols-2 xl:grid-cols-3">
              <section>
                <h3 className="text-sm font-medium">現状の課題</h3>
                <div className="mt-2">
                  <EvidenceBackedList
                    items={workstream.issues}
                    empty="課題は未登録です。有無は未確認です"
                    evidenceRefs={workstream.evidenceRefs}
                    onOpenSources={onOpenSources}
                  />
                </div>
              </section>
              <section>
                <h3 className="text-sm font-medium">今週のアジェンダ</h3>
                <div className="mt-2">
                  <EvidenceBackedList
                    items={workstream.weeklyAgenda}
                    empty="アジェンダは未登録です"
                    evidenceRefs={workstream.evidenceRefs}
                    onOpenSources={onOpenSources}
                  />
                </div>
              </section>
              <section>
                <h3 className="text-sm font-medium">判断してほしいこと</h3>
                <div className="mt-2">
                  <EvidenceBackedList
                    items={workstream.decisionRequests}
                    empty="判断依頼は未登録です。有無は未確認です"
                    evidenceRefs={workstream.evidenceRefs}
                    onOpenSources={onOpenSources}
                  />
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
  )
}

function DecisionsActionsPanel({
  snapshot,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  onOpenSources: () => void
}) {
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className="space-y-3">
        <h2 className="font-semibold">判断事項</h2>
        {snapshot.decisions.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">判断事項は未登録です</p>
        ) : (
          <div className="divide-y rounded-lg border bg-card shadow-sm">
            {snapshot.decisions.map((decision) => {
              const claim = { text: decision.summary, evidenceIds: decision.evidenceIds, unknownReason: decision.unknownReason }
              const resolved = resolveClaim(claim, evidenceRefs)
              return (
                <div key={decision.decisionId} className="space-y-2 p-4">
                  {resolved.displayable && (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-muted-foreground">担当 {ownerLabel(decision.ownerRef, decision.ownerDisplayName)}</p>
                      <Badge tone={decision.state === 'decided' ? 'success' : decision.state === 'deferred' ? 'warning' : 'default'} className="rounded-md">
                        {decision.state === 'decided' ? '決定済み' : decision.state === 'deferred' ? '保留' : '判断待ち'}
                      </Badge>
                    </div>
                  )}
                  <EvidenceBackedValue claim={claim} evidenceRefs={evidenceRefs} onOpenSources={onOpenSources} />
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold">アクション</h2>
        {snapshot.actionItems.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">アクションは未登録です</p>
        ) : (
          <div className="divide-y rounded-lg border bg-card shadow-sm">
            {snapshot.actionItems.map((action) => {
              const claim = { text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }
              const resolved = resolveClaim(claim, evidenceRefs)
              return (
                <div key={action.actionItemId} className="space-y-2 p-4">
                  {resolved.displayable && (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-muted-foreground">
                        担当 {ownerLabel(action.ownerRef, action.ownerDisplayName)}
                        <span className="mx-2" aria-hidden>/</span>
                        期限 <span className="tabular-nums">{formatDateTime(action.dueAt)}</span>
                      </p>
                      <Badge tone={action.status === 'completed' ? 'success' : action.status === 'blocked' ? 'destructive' : action.status === 'cancelled' ? 'muted' : 'warning'} className="rounded-md">
                        {action.status === 'completed' ? '完了' : action.status === 'blocked' ? '停止中' : action.status === 'cancelled' ? '取消' : '未完了'}
                      </Badge>
                    </div>
                  )}
                  <EvidenceBackedValue claim={claim} evidenceRefs={evidenceRefs} onOpenSources={onOpenSources} />
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

function safeSourceLabel(source: SourceRun, index: number): string {
  if (source.state === 'denied') return '権限により非表示の情報源'
  if (source.state === 'error') return '取得エラーの情報源'
  return `情報源 ${index + 1}`
}

function SourcesPanel({ snapshot }: { snapshot: MeetingSnapshot }) {
  const deniedCount = snapshot.sourceRuns.filter((run) => run.state === 'denied').length
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold">情報源の鮮度</h2>
        <p className="mt-1 text-sm text-muted-foreground">取得失敗や未設定も消さずに表示します。</p>
      </div>
      {deniedCount > 0 && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm" role="status">
          <ShieldAlert className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          権限により非表示の情報源があります
        </div>
      )}
      {snapshot.sourceRuns.length === 0 ? (
        <p className="rounded-md border p-4 text-sm text-muted-foreground">情報源の確認履歴はまだありません</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {snapshot.sourceRuns.map((source, index) => {
            const restricted = source.state === 'denied'
            return (
              <div key={source.sourceRunId} className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-medium">{safeSourceLabel(source, index)}</h3>
                  <StateBadge state={source.state} />
                </div>
                {!restricted && (
                  <>
                    {source.purpose?.trim() && (
                      <p className="mt-2 text-sm text-muted-foreground">{source.purpose}</p>
                    )}
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
                      <div>
                        <dt className="text-muted-foreground">確認時刻</dt>
                        <dd className="mt-1 tabular-nums">{formatDateTime(source.observedThrough)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">最終成功</dt>
                        <dd className="mt-1 tabular-nums">{formatDateTime(source.lastSuccessfulAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">根拠件数</dt>
                        <dd className="mt-1 tabular-nums">{source.evidenceCount ?? '未確認'}</dd>
                      </div>
                    </dl>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function HistoricalEvidenceValue({
  claim,
  evidenceRefs,
}: {
  claim: EvidenceBackedText
  evidenceRefs: EvidenceRef[]
}) {
  return (
    <EvidenceBackedValue
      claim={claim}
      evidenceRefs={evidenceRefs}
      onOpenSources={() => undefined}
      allowDrillthrough={false}
    />
  )
}

function historicalGateEvidenceState(
  gate: Workstream['outcomeGateSet']['gates'][number],
  evidenceRefs: EvidenceRef[],
): MeetingEvidenceState {
  if (gate.completionEvidenceIds.length === 0) return 'unknown'
  return resolveClaim({
    text: gate.title,
    evidenceIds: gate.completionEvidenceIds,
    unknownReason: null,
  }, evidenceRefs).state
}

function HistoricalSnapshotContent({ snapshot }: { snapshot: MeetingSnapshot }) {
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <div className="space-y-6" data-testid="historical-snapshot-content">
      <header className="space-y-3 border-b pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">{formatDate(snapshot.weekOf)}の週</h3>
            <p className="mt-1 text-xs tabular-nums text-muted-foreground">
              改訂 {snapshot.revision} · 保存 {formatDateTime(snapshot.createdAt)}
            </p>
          </div>
          <Badge tone={snapshot.lifecycle === 'locked' ? 'success' : snapshot.lifecycle === 'blocked' ? 'destructive' : 'muted'} className="rounded-md">
            {LIFECYCLE_LABELS[snapshot.lifecycle]}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium tabular-nums">
            必須情報源 {snapshot.requiredVerifiedSourceCount}/{snapshot.requiredSourceCount}
          </span>
          <StateBadge state={snapshot.requiredSourceState} />
          <span className="text-xs text-muted-foreground">この改訂で保存された状態</span>
        </div>
      </header>

      <section className="space-y-3" aria-labelledby="historical-workstreams-title">
        <h4 id="historical-workstreams-title" className="text-sm font-semibold">取組の記録</h4>
        <div className="grid gap-4">
          {snapshot.workstreams.map((workstream) => (
            <article key={workstream.workstreamId} className="rounded-md border bg-muted/20 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h5 className="font-medium">{workstream.title}</h5>
                  <p className="mt-1 text-xs text-muted-foreground">
                    担当 {ownerLabel(workstream.ownerRef, workstream.ownerDisplayName)}
                  </p>
                </div>
                <Badge tone={workstream.outcomeGateSet.approved ? 'success' : 'warning'} className="rounded-md">
                  {workstream.outcomeGateSet.approved ? '成果ゲート承認済み' : '成果ゲート定義中'}
                </Badge>
              </div>

              <dl className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">当時の現在地</dt>
                  <dd className="mt-1">
                    <HistoricalEvidenceValue claim={workstream.currentState} evidenceRefs={workstream.evidenceRefs} />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">当時のスケジュール</dt>
                  <dd className="mt-1">
                    <HistoricalEvidenceValue claim={workstream.schedule} evidenceRefs={workstream.evidenceRefs} />
                  </dd>
                </div>
              </dl>

              <div className="mt-4 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground">成果ゲート</p>
                {workstream.outcomeGateSet.gates.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">成果ゲートは未登録です</p>
                ) : (
                  <ul className="mt-2 grid gap-2">
                    {workstream.outcomeGateSet.gates.map((gate) => {
                      const evidenceState = historicalGateEvidenceState(gate, workstream.evidenceRefs)
                      return (
                        <li key={gate.gateId} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border bg-card px-3 py-2 text-sm">
                          <span>{gate.title}</span>
                          <span className="flex flex-wrap items-center gap-2">
                            <Badge tone={gate.status === 'completed' ? 'success' : gate.status === 'blocked' ? 'destructive' : 'muted'} className="rounded-md">
                              {gate.status === 'completed' ? '完了' : gate.status === 'blocked' ? '停止中' : '未完了'}
                            </Badge>
                            <StateBadge state={evidenceState} />
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        <section className="space-y-3" aria-labelledby="historical-decisions-title">
          <h4 id="historical-decisions-title" className="text-sm font-semibold">判断事項</h4>
          {snapshot.decisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">判断事項は未登録です</p>
          ) : snapshot.decisions.map((decision) => (
            <div key={decision.decisionId} className="rounded-md border p-3">
              <HistoricalEvidenceValue
                claim={{ text: decision.summary, evidenceIds: decision.evidenceIds, unknownReason: decision.unknownReason }}
                evidenceRefs={evidenceRefs}
              />
            </div>
          ))}
        </section>
        <section className="space-y-3" aria-labelledby="historical-actions-title">
          <h4 id="historical-actions-title" className="text-sm font-semibold">アクション</h4>
          {snapshot.actionItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">アクションは未登録です</p>
          ) : snapshot.actionItems.map((action) => (
            <div key={action.actionItemId} className="rounded-md border p-3">
              <HistoricalEvidenceValue
                claim={{ text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }}
                evidenceRefs={evidenceRefs}
              />
            </div>
          ))}
        </section>
      </div>
    </div>
  )
}

function HistoryPanel({
  workspaceId,
}: {
  workspaceId: string
}) {
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null)
  const historyQuery = useMeetingCenterHistory(workspaceId)
  const snapshotQuery = useMeetingCenterSnapshot(workspaceId, selectedSnapshotId)

  if (historyQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">履歴を読み込んでいます</p>
  }
  if (historyQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
        履歴を取得できませんでした
      </div>
    )
  }
  const items = historyQuery.data?.items ?? []
  if (items.length === 0) {
    return <p className="rounded-md border p-4 text-sm text-muted-foreground">過去回はまだありません</p>
  }
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(16rem,0.45fr)_minmax(0,1fr)]">
      <div className="self-start divide-y rounded-lg border bg-card shadow-sm">
        {items.map((item) => (
          <button
            key={item.snapshotId}
            type="button"
            onClick={() => setSelectedSnapshotId(item.snapshotId)}
            className={cn(
              'flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring active:translate-y-px',
              selectedSnapshotId === item.snapshotId && 'bg-muted',
            )}
          >
            <span>
              <span className="block font-medium">{formatDate(item.weekOf)}の週</span>
              <span className="mt-1 block text-xs tabular-nums text-muted-foreground">改訂 {item.revision}</span>
            </span>
            <Badge tone={item.lifecycle === 'locked' ? 'success' : item.lifecycle === 'blocked' ? 'destructive' : 'muted'} className="rounded-md">
              {LIFECYCLE_LABELS[item.lifecycle]}
            </Badge>
          </button>
        ))}
      </div>
      <div className="rounded-lg border bg-card p-5 shadow-sm">
        {!selectedSnapshotId ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <History className="h-5 w-5" aria-hidden />
            確認する週を選んでください
          </div>
        ) : snapshotQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">選択した改訂を読み込んでいます</p>
        ) : snapshotQuery.isError || !snapshotQuery.data ? (
          <p className="text-sm text-destructive">選択した改訂を取得できませんでした</p>
        ) : (
          <HistoricalSnapshotContent snapshot={snapshotQuery.data.snapshot} />
        )}
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div aria-busy="true" className="space-y-4" role="status">
      <p className="text-sm text-muted-foreground">週次スナップショットを読み込んでいます</p>
      <div className="h-24 animate-pulse rounded-lg border bg-muted/50 motion-reduce:animate-none" />
      <div className="h-52 animate-pulse rounded-lg border bg-muted/50 motion-reduce:animate-none" />
    </div>
  )
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="flex items-start gap-3">
        <CalendarClock className="h-6 w-6 shrink-0 text-muted-foreground" aria-hidden />
        <div>
          <h2 className="font-semibold">今週の下書きはまだありません</h2>
          <p className="mt-1 text-sm text-muted-foreground">情報源を確認し、更新Skillで下書きを作成してください。</p>
        </div>
      </CardContent>
    </Card>
  )
}

function ErrorState({ error }: { error: unknown }) {
  const denied = error instanceof ApiError && error.status === 403
  return (
    <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-5 text-sm">
      <div className="flex items-start gap-3">
        {denied
          ? <ShieldAlert className="h-6 w-6 shrink-0 text-destructive" aria-hidden />
          : <XCircle className="h-6 w-6 shrink-0 text-destructive" aria-hidden />}
        <div>
          <h2 className="font-semibold text-destructive">
            {denied ? '会議・報告を閲覧できません' : '会議・報告を取得できませんでした'}
          </h2>
          <p className="mt-1 text-muted-foreground">
            {denied ? '所属と閲覧権限を管理者へ確認してください。' : '接続状態を確認してから再度お試しください。'}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">情報源の鮮度は取得できていません</p>
        </div>
      </div>
    </div>
  )
}

export function MeetingCenterPage({
  surface = 'meeting-center',
}: {
  surface?: MeetingCenterSurface
}) {
  const [audience, setAudience] = useState<MeetingAudience>('field')
  const [isPresenting, setIsPresenting] = useState(false)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const sourcesTabRef = useRef<HTMLButtonElement>(null)
  const presentationRef = useRef<HTMLDivElement>(null)
  const presentationToggleRef = useRef<HTMLButtonElement>(null)
  const requestedTab = searchParams.get('tab')
  const surfaceCopy = SURFACE_COPY[surface]
  const workspace = useResolvedWorkspace()
  const latestQuery = useMeetingCenterLatest(workspace.workspaceId)
  const response = latestQuery.data
  const snapshot = response?.snapshot
  const allowsWorkstreams = surface !== 'evidence-history'
  const allowedBaseTabs: readonly BaseMeetingTab[] = surface === 'weekly-status'
    ? ['current']
    : surface === 'evidence-history'
      ? ['sources', 'history']
      : ['current', 'actions', 'sources', 'history']
  const requestedWorkstreamExists = Boolean(
    allowsWorkstreams && snapshot?.workstreams.some(
      (workstream) => workstreamTabValue(workstream.workstreamId) === requestedTab,
    ),
  )
  const activeTab = allowedBaseTabs.includes(requestedTab as BaseMeetingTab) || requestedWorkstreamExists
    ? requestedTab!
    : surfaceCopy.defaultTab

  const snapshotSourceState = useMemo(
    () => snapshot ? sourceSummaryState(snapshot) : 'unknown',
    [snapshot],
  )

  useEffect(() => {
    if (!isPresenting) return

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    const focusRestoreTarget = presentationToggleRef.current ?? previouslyFocused
    const previousBodyOverflow = document.body.style.overflow
    const previousPresentationState = document.body.dataset.meetingPresenting
    const chrome = Array.from(document.querySelectorAll<HTMLElement>('[data-app-chrome]'))
      .map((element) => ({
        element,
        ariaHidden: element.getAttribute('aria-hidden'),
        inert: element.inert === true,
      }))

    document.body.style.overflow = 'hidden'
    document.body.dataset.meetingPresenting = 'true'
    for (const state of chrome) {
      state.element.inert = true
      state.element.setAttribute('aria-hidden', 'true')
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsPresenting(false)
        return
      }
      if (event.key !== 'Tab') return

      const focusable = Array.from(
        presentationRef.current?.querySelectorAll<HTMLElement>(PRESENTATION_FOCUSABLE_SELECTOR) ?? [],
      ).filter((element) => !element.closest('[hidden],[aria-hidden="true"]'))
      if (focusable.length === 0) {
        event.preventDefault()
        presentationRef.current?.focus()
        return
      }
      const first = focusable[0]!
      const last = focusable.at(-1)!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (!presentationRef.current?.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    queueMicrotask(() => presentationToggleRef.current?.focus())

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousBodyOverflow
      if (previousPresentationState === undefined) {
        delete document.body.dataset.meetingPresenting
      } else {
        document.body.dataset.meetingPresenting = previousPresentationState
      }
      for (const state of chrome) {
        state.element.inert = state.inert
        if (state.ariaHidden === null) state.element.removeAttribute('aria-hidden')
        else state.element.setAttribute('aria-hidden', state.ariaHidden)
      }
      queueMicrotask(() => {
        if (focusRestoreTarget?.isConnected) focusRestoreTarget.focus()
      })
    }
  }, [isPresenting])

  const changeTab = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value === 'current') next.delete('tab')
    else next.set('tab', value)
    setSearchParams(next, { replace: true })
  }
  const openSources = () => {
    if (surface !== 'evidence-history') {
      navigate('/evidence-history')
      return
    }
    changeTab('sources')
    queueMicrotask(() => sourcesTabRef.current?.focus())
  }
  const copyQuestion = async () => {
    if (!snapshot) return
    try {
      await navigator.clipboard.writeText(buildMeetingQuestionPrompt(snapshot, audience))
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  return (
    <div
      ref={presentationRef}
      data-meeting-surface={surface}
      data-presentation-mode={isPresenting ? 'true' : 'false'}
      role={isPresenting ? 'dialog' : undefined}
      aria-modal={isPresenting ? 'true' : undefined}
      aria-label={isPresenting ? '週次会議 投影モード' : undefined}
      tabIndex={isPresenting ? -1 : undefined}
      className={cn(
        'mx-auto w-full max-w-[1440px] space-y-6',
        isPresenting &&
          'fixed inset-0 z-50 max-w-none overflow-y-auto bg-white p-5 text-slate-950 sm:p-8 lg:p-12 [&_.text-muted-foreground]:text-slate-700 [&_.text-sm]:text-base [&_button]:text-base',
      )}
    >
      <header className="space-y-4 border-b pb-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className={cn('text-2xl font-bold', isPresenting && 'text-3xl lg:text-4xl')}>
              {surfaceCopy.title}
            </h1>
            <p className="mt-1 text-muted-foreground">{surfaceCopy.description}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {surface !== 'evidence-history' && (
              <div className="inline-flex w-fit rounded-md border bg-card p-1" aria-label="表示対象">
                <button
                  type="button"
                  aria-pressed={audience === 'field'}
                  aria-label="現場向け表示"
                  onClick={() => setAudience('field')}
                  className={cn(
                    'whitespace-nowrap rounded-sm px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px',
                    audience === 'field' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  現場
                </button>
                <button
                  type="button"
                  aria-pressed={audience === 'executive'}
                  aria-label="役員向け表示"
                  onClick={() => setAudience('executive')}
                  className={cn(
                    'whitespace-nowrap rounded-sm px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px',
                    audience === 'executive' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  役員
                </button>
              </div>
            )}
            {surface === 'meeting-center' && (
              <>
                <button
                  type="button"
                  onClick={() => void copyQuestion()}
                  className="inline-flex min-h-10 items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Copy className="h-4 w-4" aria-hidden />
                  {copyState === 'copied' ? '質問文をコピー済み' : 'AIへの質問文をコピー'}
                </button>
                <button
                  ref={presentationToggleRef}
                  type="button"
                  aria-pressed={isPresenting}
                  onClick={() => setIsPresenting((current) => !current)}
                  className="inline-flex min-h-10 items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {isPresenting
                    ? <Minimize2 className="h-4 w-4" aria-hidden />
                    : <Maximize2 className="h-4 w-4" aria-hidden />}
                  {isPresenting ? '投影を終了' : '投影モード'}
                </button>
                <span className="sr-only" role="status" aria-live="polite">
                  {copyState === 'failed' ? '質問文をコピーできませんでした' : copyState === 'copied' ? '質問文をコピーしました' : ''}
                </span>
              </>
            )}
          </div>
        </div>

        {snapshot && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <span className="font-medium tabular-nums">{formatDate(snapshot.weekOf)}の週</span>
            <Badge tone={snapshot.lifecycle === 'ready' || snapshot.lifecycle === 'locked' ? 'success' : snapshot.lifecycle === 'blocked' ? 'destructive' : 'warning'} className="rounded-md">
              {LIFECYCLE_LABELS[snapshot.lifecycle]}
            </Badge>
            {snapshotSourceState !== 'verified' && <StateBadge state={snapshotSourceState} />}
            <span className="tabular-nums text-muted-foreground">最終更新 {formatDateTime(snapshot.createdAt)}</span>
            <span className="tabular-nums text-muted-foreground">改訂 {snapshot.revision}</span>
          </div>
        )}
      </header>

      {workspace.isLoading || latestQuery.isLoading ? (
        <LoadingState />
      ) : workspace.isError || latestQuery.isError ? (
        <ErrorState error={latestQuery.error ?? workspace.error} />
      ) : !workspace.workspaceId || !response || !snapshot ? (
        <EmptyState />
      ) : (
        <Tabs.Root value={activeTab} onValueChange={changeTab} orientation="horizontal">
          <Tabs.List
            aria-label="会議・報告の表示内容"
            className="flex gap-1 overflow-x-auto border-b"
          >
            {[
              ...(allowedBaseTabs.includes('current') ? [{ value: 'current', label: '今週' }] : []),
              ...(allowsWorkstreams ? snapshot.workstreams.map((workstream) => ({
                value: workstreamTabValue(workstream.workstreamId),
                label: workstream.title,
              })) : []),
              ...(allowedBaseTabs.includes('actions') ? [{ value: 'actions', label: '判断・アクション' }] : []),
              ...(allowedBaseTabs.includes('sources') ? [{ value: 'sources', label: '情報源' }] : []),
              ...(allowedBaseTabs.includes('history') ? [{ value: 'history', label: '履歴' }] : []),
            ].map(({ value, label }) => (
              <Tabs.Trigger
                key={value}
                value={value}
                ref={value === 'sources' ? sourcesTabRef : undefined}
                className="shrink-0 whitespace-nowrap border-b-2 border-transparent px-3 py-3 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring data-[state=active]:border-primary data-[state=active]:text-foreground"
              >
                {label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <Tabs.Content value="current" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <CurrentWeekPanel snapshot={snapshot} audience={audience} onOpenSources={openSources} />
          </Tabs.Content>
          {allowsWorkstreams && snapshot.workstreams.map((workstream) => (
            <Tabs.Content
              key={workstream.workstreamId}
              value={workstreamTabValue(workstream.workstreamId)}
              className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <WorkstreamPanel workstream={workstream} onOpenSources={openSources} />
            </Tabs.Content>
          ))}
          <Tabs.Content value="actions" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <DecisionsActionsPanel snapshot={snapshot} onOpenSources={openSources} />
          </Tabs.Content>
          <Tabs.Content value="sources" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <SourcesPanel snapshot={snapshot} />
          </Tabs.Content>
          <Tabs.Content value="history" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <HistoryPanel workspaceId={workspace.workspaceId} />
          </Tabs.Content>
        </Tabs.Root>
      )}
    </div>
  )
}
